import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Search, X, Loader2 } from 'lucide-react';
import { cn } from '../../utils/cn';
import Input from './Input';

// Simple debounce hook
const useDebounce = (value, delay = 400) => {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const id = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(id);
  }, [value, delay]);
  return debounced;
};

/**
 * CityAutocomplete - Recherche de communes françaises via l'API gouvernementale geo.api.gouv.fr
 * Props:
 *  - value: { code, nom } ou string (code INSEE) selon usage
 *  - onChange: (cityObject|null) => void
 *  - label, placeholder, required, error, className
 */
const CityAutocomplete = ({
  value = null,
  onChange,
  label = 'Ville',
  placeholder = 'Rechercher une commune (ex: Nantes)',
  required = false,
  error,
  className,
  disabled = false,
  minLength = 2,
  limit = 8,
}) => {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [isOpen, setIsOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [fetchError, setFetchError] = useState(null);

  const containerRef = useRef(null);
  const debouncedQuery = useDebounce(query);

  const selectedLabel = value?.nom || '';

  const fetchCities = useCallback(async (term) => {
    if (!term || term.length < minLength) {
      setResults([]);
      return;
    }
    setLoading(true);
    setFetchError(null);
    try {
      const url = `https://geo.api.gouv.fr/communes?nom=${encodeURIComponent(term)}&fields=code,nom,codesPostaux,centre,population&boost=population&limit=${limit}`;
      const resp = await fetch(url, { headers: { 'Accept': 'application/json' } });
      if (!resp.ok) throw new Error('Erreur API');
      const data = await resp.json();
      setResults(data || []);
    } catch (e) {
      setFetchError(e.message || 'Erreur');
    } finally {
      setLoading(false);
    }
  }, [limit, minLength]);

  // Fetch when debounced query changes
  useEffect(() => { fetchCities(debouncedQuery); }, [debouncedQuery, fetchCities]);

  // Close on outside click
  useEffect(() => {
    const handleClick = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  const handleSelect = (city) => {
    onChange?.(city);
    setQuery(city.nom);
    setIsOpen(false);
  };

  const handleClear = (e) => {
    e.stopPropagation();
    setQuery('');
    onChange?.(null);
    setResults([]);
    setIsOpen(false);
  };

  return (
    <div className={cn('relative', className)} ref={containerRef}>
      {label && (
        <label className={cn('block text-sm font-medium mb-2', error ? 'text-destructive' : 'text-foreground')}>
          {label} {required && <span className="text-destructive">*</span>}
        </label>
      )}
      <div className={cn('flex items-center gap-2 rounded-md border px-3 py-2 bg-white text-black focus-within:ring-2 transition', error ? 'border-destructive focus-within:ring-destructive' : 'border-input focus-within:ring-primary')}>
        <Search className="h-4 w-4 text-muted-foreground" />
        <input
          type="text"
          className="flex-1 bg-transparent outline-none text-sm placeholder:text-muted-foreground"
          placeholder={placeholder}
          value={query}
          disabled={disabled}
          onChange={(e) => { setQuery(e.target.value); setIsOpen(true); }}
          onFocus={() => { if (query.length >= minLength) setIsOpen(true); }}
        />
        {loading && <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />}
        {query && !loading && (
          <button type="button" onClick={handleClear} className="p-1 rounded hover:bg-muted transition">
            <X className="h-4 w-4 text-muted-foreground" />
          </button>
        )}
      </div>
      {error && <p className="text-sm text-destructive mt-1">{error}</p>}
      {!error && fetchError && <p className="text-xs text-destructive mt-1">{fetchError}</p>}
      <p className="text-xs text-muted-foreground mt-1">Tapez au moins {minLength} lettres</p>

      {isOpen && results.length > 0 && (
        <div className="absolute z-50 mt-1 w-full bg-white border border-border rounded-md shadow-lg max-h-64 overflow-auto">
          {results.map((city) => (
            <button
              key={city.code}
              type="button"
              onClick={() => handleSelect(city)}
              className={cn('w-full text-left px-3 py-2 text-sm hover:bg-accent flex flex-col')}
            >
              <span className="font-medium text-foreground">{city.nom}</span>
              <span className="text-xs text-muted-foreground">CP: {city.codesPostaux?.join(', ')} • Pop. {city.population?.toLocaleString('fr-FR')}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default CityAutocomplete;
